import { type TypedDocumentNode, gql } from '@apollo/client';
import classNames from 'classnames';
import { Fragment } from 'react';
import { styled } from 'styled-components';

import { InningHalf } from '@sorare/core/src/__generated__/globalTypes';
import { LabelM } from '@sorare/core/src/atoms/typography';
import { range } from '@sorare/core/src/lib/arrays';
import {
  laptopAndAbove,
  mediumLargeDesktopAndAbove,
} from '@sorare/core/src/style/mediaQuery';

import type { ScoresByInning_GameOfBaseball } from './__generated__/index.graphql';

const Text = styled(LabelM)`
  color: var(--c-nd-600);
  font-variant-numeric: tabular-nums;
`;
const Root = styled.div`
  display: grid;
  grid-template-rows: repeat(3, 1fr);
  grid-auto-flow: column;
  row-gap: var(--unit);
  column-gap: var(--unit);
  text-align: center;
  overflow-x: auto;

  /* first column with team abbreviations */
  & > *:nth-child(-n + 3) {
    min-width: 4ch;
  }

  & > * {
    min-width: 2ch;
  }

  .white {
    color: var(--c-white);
  }
  .faded {
    color: var(--c-nd-300);
  }
  .bold {
    font-weight: var(--t-bold);
  }

  @media ${laptopAndAbove} {
    column-gap: calc(var(--unit) * 5);
  }

  @media ${mediumLargeDesktopAndAbove} {
    column-gap: calc(var(--unit) * 7);
  }
`;

type Props = { game: ScoresByInning_GameOfBaseball };

export const ScoresByInning = ({ game }: Props) => {
  const scoresByInning = game.scoresByInning ?? [];
  const innings = Math.max(scoresByInning.length, 9);
  const gameInning = game.inning?.number ?? 0;
  const gameInningHalf = game.inning?.half ?? '';

  return (
    <Root>
      <div />
      <Text className="white bold">{game.awayTeam?.code}</Text>
      <Text className="white bold">{game.homeTeam?.code}</Text>
      {range(innings).map(index => {
        const inning = scoresByInning[index];
        const periodNumber = inning?.periodNumber ?? index + 1;
        const awayScore = inning?.awayScore ?? 0;
        const homeScore = inning?.homeScore ?? 0;
        const isLiveInning = periodNumber === gameInning;

        const isFutureInning = !inning || periodNumber > gameInning;
        const isFutureInningHalf =
          isFutureInning || (isLiveInning && gameInningHalf === InningHalf.TOP);
        return (
          <Fragment key={periodNumber}>
            <Text
              className={classNames({
                faded: isFutureInning,
              })}
            >
              {periodNumber}
            </Text>
            <Text
              className={classNames({
                white: !isFutureInning,
                faded: isFutureInning,
                bold: true,
              })}
            >
              {isFutureInning ? '-' : awayScore}
            </Text>
            <Text
              className={classNames({
                white: !isFutureInningHalf,
                faded: isFutureInningHalf,
                bold: true,
              })}
            >
              {isFutureInningHalf ? '-' : homeScore}
            </Text>
          </Fragment>
        );
      })}
    </Root>
  );
};

ScoresByInning.fragments = {
  GameOfBaseball: gql`
    fragment ScoresByInning_GameOfBaseball on GameOfBaseball {
      id
      inning {
        number
        half
      }
      awayTeam {
        slug
        code
      }
      homeTeam {
        slug
        code
      }
      scoresByInning {
        homeScore
        awayScore
        periodNumber
      }
    }
  ` as TypedDocumentNode<ScoresByInning_GameOfBaseball>,
};
